<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import AiAgentRunsAPI from 'dashboard/api/aiAgents/runs';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const agent = useMapGetter('aiAgents/getAgent');
const runs = ref([]);
const isLoading = ref(false);

const agentId = computed(() => Number(route.params.id));
const selectedAgent = computed(() => agent.value(agentId.value));

const loadRuns = async () => {
  isLoading.value = true;
  try {
    const { data } = await AiAgentRunsAPI.index({
      page: 1,
      ai_agent_id: agentId.value,
    });
    runs.value = data.payload || [];
  } finally {
    isLoading.value = false;
  }
};

const goBack = () => {
  router.push({ name: 'ai_agents_edit', params: { id: agentId.value } });
};

onMounted(async () => {
  await store.dispatch('aiAgents/show', agentId.value);
  await loadRuns();
});
</script>

<template>
  <div class="flex flex-col h-full gap-4 p-6 overflow-y-auto">
    <div class="flex items-center gap-3">
      <Button
        icon="i-lucide-arrow-left"
        slate
        ghost
        size="sm"
        @click="goBack"
      />
      <div>
        <h1 class="m-0 text-lg font-semibold text-n-slate-12">
          {{ t('AI_AGENTS.RUNS.TITLE') }}
        </h1>
        <p v-if="selectedAgent" class="m-0 text-sm text-n-slate-11">
          {{ selectedAgent.name }}
        </p>
      </div>
    </div>
    <div v-if="isLoading" class="text-sm text-n-slate-11">...</div>
    <p v-else-if="!runs.length" class="text-sm text-n-slate-11">
      {{ t('AI_AGENTS.RUNS.EMPTY') }}
    </p>
    <table v-else class="w-full text-sm border-collapse">
      <thead>
        <tr class="text-left border-b border-n-weak text-n-slate-11">
          <th class="py-2">{{ t('AI_AGENTS.RUNS.MODE') }}</th>
          <th class="py-2">{{ t('AI_AGENTS.RUNS.STATUS') }}</th>
          <th class="py-2">{{ t('AI_AGENTS.RUNS.TOKENS') }}</th>
          <th class="py-2">{{ t('AI_AGENTS.RUNS.COST') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="run in runs"
          :key="run.id"
          class="border-b border-n-weak text-n-slate-12"
        >
          <td class="py-2">{{ run.mode }}</td>
          <td class="py-2">{{ run.status }}</td>
          <td class="py-2">{{ run.total_tokens }}</td>
          <td class="py-2">{{ run.estimated_cost_usd }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
